import { useCallback, useMemo } from "react"

import { getDisplayedRunStatus, getStatusLabel } from "@/runs/status"

import type { RunsTableResult } from "@/types/tables/RunsTable"
import type { SimulationsTableResult } from "@/types/tables/SimulationsTable"
import {
  convertTableDataToCSV,
  downloadCSV,
  type TableCsvColumn,
} from "@/utils/csv/csv-export"
import { toISODateString } from "@/utils/date/date-range-helpers"

import type { ColumnDef } from "../types/columns"
import { RUNS_VIEW_MODE, type RunsViewMode } from "../types/viewMode"
import { formatMetricValue } from "../utils/metricColumnUtils"
import { isMetricColumn } from "../utils/virtualRunsTableLayout"

interface RunsTableCsvLookups {
  agentNames: Record<string, string>
  testSetNames: Record<string, string>
  personasById: Record<string, { display_name?: string } | undefined>
  getModelType: (modelType: string) => string
  resolveCreator: (creator: string | null | undefined) => string
}

interface UseRunsTableCsvDownloadParams extends RunsTableCsvLookups {
  columns: ColumnDef[]
  runs: RunsTableResult[]
  simulations: SimulationsTableResult[]
  visibleColumns: Set<string>
  viewMode: RunsViewMode
}

function resourceName(
  id: string | null | undefined,
  names: Record<string, string>,
): string {
  return id ? names[id] || id : "-"
}

function personaName(
  id: string | null | undefined,
  personasById: RunsTableCsvLookups["personasById"],
): string {
  return id ? personasById[id]?.display_name || id : "-"
}

function runName(run: RunsTableResult): string {
  return (
    run.display_name?.trim() ||
    `Run on ${new Date(run.created_at).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })}`
  )
}

function runValue(
  run: RunsTableResult,
  columnKey: string,
  lookups: RunsTableCsvLookups,
): unknown {
  if (isMetricColumn(columnKey)) {
    const value = run.metric_averages?.[columnKey.slice("metric:".length)]
    return typeof value === "number" && isFinite(value)
      ? formatMetricValue(value)
      : ""
  }

  switch (columnKey) {
    case "status":
      return getStatusLabel(getDisplayedRunStatus(run.status, run.error_status))
    case "runName":
      return runName(run)
    case "agentName":
      return resourceName(run.agent_id, lookups.agentNames)
    case "testSetName":
      return resourceName(run.test_set_id, lookups.testSetNames)
    case "personaId":
      return personaName(run.persona_id_id, lookups.personasById)
    case "modelType":
      return lookups.getModelType(run.model_type)
    case "numSimulations":
      return run.num_simulations || "-"
    case "created":
      return run.created_at
    case "createdBy":
      return lookups.resolveCreator(run.created_by_user_id ?? run.created_by)
    case "tags":
      return run.tags?.join(", ") ?? ""
    case "id":
      return run.id
    default:
      return ""
  }
}

function simulationValue(
  simulation: SimulationsTableResult,
  columnKey: string,
  lookups: RunsTableCsvLookups,
): unknown {
  if (isMetricColumn(columnKey)) {
    const value = simulation.metric_values?.[columnKey.slice("metric:".length)]
    return typeof value === "number" && isFinite(value)
      ? formatMetricValue(value)
      : ""
  }

  switch (columnKey) {
    case "status":
      return getStatusLabel(simulation.status)
    case "agentName":
      return resourceName(simulation.agent_id, lookups.agentNames)
    case "testSetName":
      return resourceName(simulation.test_set_id, lookups.testSetNames)
    case "personaId":
      return personaName(simulation.persona_id_id, lookups.personasById)
    case "modelType":
      return lookups.getModelType(simulation.model_type)
    case "created":
      return simulation.created_at
    case "createdBy":
      return lookups.resolveCreator(
        simulation.created_by_user_id ?? simulation.created_by,
      )
    case "tags":
      return simulation.tags?.join(", ") ?? ""
    case "callSid":
      return simulation.call_sid ?? ""
    case "simulationId":
      return simulation.simulation_output_id
    case "runId":
      return simulation.run_id
    default:
      return ""
  }
}

export function useRunsTableCsvDownload({
  columns,
  runs,
  simulations,
  visibleColumns,
  viewMode,
  ...lookups
}: UseRunsTableCsvDownloadParams): {
  downloadCsv: () => void
  /** Each column's header in the downloaded file, keyed by column key, so the download dialog lists what the file contains. */
  csvHeaderById: Record<string, string>
} {
  const csvHeaderById = useMemo(
    () =>
      Object.fromEntries(
        columns.map((column) => [column.key, column.csvHeader ?? column.label]),
      ),
    [columns],
  )
  const downloadCsv = useCallback(() => {
    const columnsToExport = columns.filter((column) =>
      visibleColumns.has(column.key),
    )
    const timestamp = toISODateString()

    if (viewMode === RUNS_VIEW_MODE.RUNS) {
      const csvColumns: TableCsvColumn<RunsTableResult>[] = columnsToExport.map(
        (column) => ({
          header: csvHeaderById[column.key],
          value: (run) => runValue(run, column.key, lookups),
        }),
      )
      downloadCSV(
        convertTableDataToCSV(runs, csvColumns),
        `runs-view-${timestamp}.csv`,
      )
      return
    }

    const csvColumns: TableCsvColumn<SimulationsTableResult>[] =
      columnsToExport.map((column) => ({
        header: csvHeaderById[column.key],
        value: (simulation) => simulationValue(simulation, column.key, lookups),
      }))
    downloadCSV(
      convertTableDataToCSV(simulations, csvColumns),
      `simulations-view-${timestamp}.csv`,
    )
  }, [
    columns,
    csvHeaderById,
    lookups,
    runs,
    simulations,
    viewMode,
    visibleColumns,
  ])
  return { downloadCsv, csvHeaderById }
}
